import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface InventoryGroup {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Modal Component
function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full">
          <div className="border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Group Form
function GroupForm({
  group,
  onSave,
  onCancel,
}: {
  group: Partial<InventoryGroup> | null;
  onSave: (data: { name: string; description?: string; sortOrder?: number; isActive: boolean }) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: group?.name || '',
    description: group?.description || '',
    sortOrder: group?.sortOrder ?? 0,
    isActive: group?.isActive !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              name === 'sortOrder' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Group name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Group Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          placeholder="e.g., Extrusions"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={2}
          placeholder="Optional description of this inventory group..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort Order
          </label>
          <input
            type="number"
            name="sortOrder"
            value={form.sortOrder}
            onChange={handleChange}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in tabs</p>
        </div>

        <div className="flex items-center pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : group?.id ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// Inventory Groups Page
export function InventoryGroupsPage() {
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<InventoryGroup | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/inventory-groups?includeInactive=${showInactive}`);
      setGroups(data || []);
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [showInactive]);

  const handleSaveGroup = async (data: { name: string; description?: string; sortOrder?: number; isActive: boolean }) => {
    if (editingGroup?.id) {
      await apiRequest(`/inventory-groups/${editingGroup.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    } else {
      await apiRequest('/inventory-groups', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
    setModalOpen(false);
    setEditingGroup(null);
    loadGroups();
  };

  const handleDeleteGroup = async (group: InventoryGroup) => {
    if (group.itemCount > 0) {
      alert(`Cannot delete "${group.name}" - ${group.itemCount} inventory items are using it. Reassign items first.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${group.name}"? This cannot be undone.`)) return;
    try {
      await apiRequest(`/inventory-groups/${group.id}`, { method: 'DELETE' });
      loadGroups();
    } catch (err: any) {
      alert(err.message || 'Failed to delete group');
    }
  };

  const handleToggleActive = async (group: InventoryGroup) => {
    try {
      await apiRequest(`/inventory-groups/${group.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !group.isActive }),
      });
      loadGroups();
    } catch (err: any) {
      alert(err.message || 'Failed to update group');
    }
  };

  return (
    <>
      <PageHeader
        title="Inventory Groups"
        subtitle="Manage inventory group categories for organizing parts"
        actions={
          <button
            onClick={() => {
              setEditingGroup(null);
              setModalOpen(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Group
          </button>
        }
      />

      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Inventory Groups</h2>
            <p className="text-sm text-gray-500">
              Groups organize inventory items into categories. They appear as tabs on the Inventory page.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Show inactive</span>
          </label>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No inventory groups found.</p>
            <p className="text-sm mt-1">Click "Add Group" to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm border-b">
                  <th className="pb-3 font-medium w-12">Order</th>
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium">Description</th>
                  <th className="pb-3 font-medium text-center">Items</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id} className={clsx('border-b hover:bg-gray-50', !group.isActive && 'opacity-60')}>
                    <td className="py-3 text-gray-500">{group.sortOrder}</td>
                    <td className="py-3">
                      <span className="font-medium text-gray-900">{group.name}</span>
                    </td>
                    <td className="py-3 text-gray-600 text-sm max-w-xs truncate">
                      {group.description || '-'}
                    </td>
                    <td className="py-3 text-center">
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        group.itemCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      )}>
                        {group.itemCount}
                      </span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleToggleActive(group)}
                        className={clsx(
                          'px-2 py-1 rounded-full text-xs font-medium transition-colors',
                          group.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                      >
                        {group.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingGroup(group);
                            setModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group)}
                          className={clsx(
                            'text-red-600 hover:text-red-800',
                            group.itemCount > 0 && 'opacity-50 cursor-not-allowed'
                          )}
                          title={group.itemCount > 0 ? `${group.itemCount} items use this group` : 'Delete group'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Groups with assigned inventory items cannot be deleted. Reassign items to a different group first, or mark the group as inactive to hide it.
            </p>
          </div>
        </div>
      </Card>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingGroup(null);
        }}
        title={editingGroup ? 'Edit Inventory Group' : 'Create Inventory Group'}
      >
        <GroupForm
          group={editingGroup}
          onSave={handleSaveGroup}
          onCancel={() => {
            setModalOpen(false);
            setEditingGroup(null);
          }}
        />
      </Modal>
    </>
  );
}

export default InventoryGroupsPage;
